import { useMutation } from '@apollo/client';
import { EmptyState } from '@components/DataTable';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Skeleton, Typography, useTheme } from '@mui/material';
import { useCallback, useState } from 'react';
import { DELETE_POST, DRAFT_POST, GET_POSTS_UPERCUT, PIN_POST, PUBLISH_POST, UNPIN_POST, UNPUBLISH_POST } from '../schema/blog';
import BlogCard from './BlogCard';

interface BlogListGridProps {
    data: any[];
    headerColumnList: {
        id: number;
        title: string;
        minWidth: string;
    }[];
    loading: boolean;
    readOnly?: boolean;
}

const BlogListGrid = ({ data, loading, readOnly }: BlogListGridProps) => {
    const theme = useTheme();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const { showSnackbar } = useSnackbar();

    const [deletePost, { loading: deleteLoading }] = useMutation(DELETE_POST);

    // Add GraphQL mutations for publish, unpublish, and pin
    const [publishPost, { loading: publishLoading }] = useMutation(PUBLISH_POST);
    const [unpublishPost, { loading: unpublishLoading }] = useMutation(UNPUBLISH_POST);
    const [pinPost, { loading: pinLoading }] = useMutation(PIN_POST);
    const [unPinPost, { loading: unPinLoading }] = useMutation(UNPIN_POST);
    const [draftPost, { loading: draftLoading }] = useMutation(DRAFT_POST); // Assuming draftPost is similar to unpublish
    // Update blog status mutation logic
    const updateBlogStatusMutate = useCallback(async (_id: string, status: string) => {
        console.log("Updating blog status:", { _id, status });
        try {
            if (readOnly) {
                showSnackbar("Read-only access. Changes are disabled.", 'warning');
                return;
            }
            if (status === 'published') {
                await publishPost({ variables: { _id } });
                showSnackbar(`L'article a été publié.`, 'success');
            } else if (status === 'unpublished') {
                await unpublishPost({ variables: { _id } });
                showSnackbar(`L'article a été dépublié.`, 'success');
            } else if (status === 'pinned') {
                await pinPost({
                    variables: { _id },
                    updateQueries: {
                        GetPostsUpercut: (prev, { mutationResult }) => {
                            const pinnedPost = mutationResult.data?.pinPost;
                            if (!pinnedPost) return prev;

                            // Unpin the previously pinned post (if any), and pin the new one
                            const updatedDocs = prev.getPostsUpercut.docs.map(post => {
                                if (post._id === pinnedPost._id) {
                                    return { ...post, isPinned: true };
                                }
                                if (post.isPinned) {
                                    return { ...post, isPinned: false };
                                }
                                return post;
                            });

                            return {
                                ...prev,
                                getPostsUpercut: {
                                    ...prev.getPostsUpercut,
                                    docs: updatedDocs,
                                },
                            };
                        },
                    },
                    refetchQueries: [{ query: GET_POSTS_UPERCUT, variables: { pagination: { offset: 0, limit: 12 }, query: '' } }],
                });
                showSnackbar(`L'article a été épinglé.`, 'success');
            } else if (status === 'unpinned') {
                await unPinPost({
                    variables: { _id },
                    updateQueries: {
                        GetPostsUpercut: (prev, { mutationResult }) => {
                            const unpinnedPost = mutationResult.data?.unPinPost;
                            if (!unpinnedPost) return prev;

                            // Unpin the post
                            const updatedDocs = prev.getPostsUpercut.docs.map(post => {
                                if (post._id === unpinnedPost._id) {
                                    return { ...post, isPinned: false };
                                }
                                return post;
                            });

                            return {
                                ...prev,
                                getPostsUpercut: {
                                    ...prev.getPostsUpercut,
                                    docs: updatedDocs,
                                },
                            };
                        },
                    },
                    refetchQueries: [{ query: GET_POSTS_UPERCUT, variables: { pagination: { offset: 0, limit: 12 }, query: '' } }],
                });
                showSnackbar(`L'article a été désépinglé.`, 'success');
            } else if (status === 'draft') {
                await draftPost({
                    variables: { _id },
                    updateQueries: {
                        GetPostsUpercut: (prev, { mutationResult }) => {
                            const draftPost = mutationResult.data?.draftPost;
                            if (!draftPost) return prev;

                            // Update the post status to draft
                            const updatedDocs = prev.getPostsUpercut.docs.map(post => {
                                if (post._id === draftPost._id) {
                                    return { ...post, status: 'draft' };
                                }
                                return post;
                            });

                            return {
                                ...prev,
                                getPostsUpercut: {
                                    ...prev.getPostsUpercut,
                                    docs: updatedDocs,
                                },
                            };
                        },
                    },
                    refetchQueries: [{ query: GET_POSTS_UPERCUT, variables: { pagination: { offset: 0, limit: 12 }, query: '' } }],
                });
                showSnackbar(`L'article a été enregistré en tant que brouillon.`, 'success');
            } else {
                showSnackbar(`Action ${status} inconnue.`, 'warning');
            }
        } catch (error) {
            showSnackbar("Une erreur s'est produite lors de la mise à jour du statut.", 'error');
            console.error("Error updating blog status:", error);
        }
    }, [publishPost, unpublishPost, pinPost, unPinPost, showSnackbar, draftPost]);

    const deleteBlogMutate = async (_id: string) => {
        try {
            if (readOnly) {
                showSnackbar("Read-only access. Changes are disabled.", 'warning');
                return;
            }
            await deletePost({
                variables: {
                    _id
                },
                updateQueries: {
                    GetPostsUpercut: (prev, { mutationResult }) => {
                        const deletedPostId = mutationResult.data?.deletePost?._id;
                        if (!deletedPostId) return prev;

                        return {
                            ...prev,
                            getPostsUpercut: {
                                ...prev.getPostsUpercut,
                                docs: prev.getPostsUpercut.docs.filter(post => post._id !== deletedPostId),
                                meta: {
                                    ...prev.getPostsUpercut.meta,
                                    totalDocs: prev.getPostsUpercut.meta.totalDocs - 1,
                                },
                            },
                        };
                    },
                }
            });
            showSnackbar("L'article a été supprimé avec succès.", 'success');
        } catch (error) {
            showSnackbar("Une erreur s'est produite lors de la suppression de l'article.", 'error');
            console.error("Error deleting blog post:", error);
        } finally {
            setOpenDialog(false);
            setSelectedId(null);

        }
    };

    const handleOpenDialog = (id: string) => {
        setSelectedId(id);
        setOpenDialog(true);
    };

    const handleCloseDialog = useCallback(() => {
        if (!deleteLoading) {
            setOpenDialog(false);
            setSelectedId(null);
        }
    }, [deleteLoading]);

    const handleConfirmDelete = () => {
        if (selectedId) {
            deleteBlogMutate(selectedId);
        }
    };

    const isLoading = publishLoading || unpublishLoading || pinLoading || unPinLoading || deleteLoading || draftLoading;

    return (
        <>
            <Grid container spacing={2} minHeight="calc(100dvh - 505px)">
                {loading && (
                    [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(val => (
                        <Grid item xs={12} sm={6} md={4} key={val} data-testid={`blog-card-skeleton-${val}`}>
                            <Card sx={{
                                minWidth: '100%',
                                height: '180px',
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: '8px',
                                border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.10)'}`,
                            }}>
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height="100%"
                                    sx={{
                                        backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.13)'
                                    }}
                                />
                            </Card>
                        </Grid>
                    ))
                )}

                {!loading && data?.length === 0 && (
                    <Grid item xs={12}>
                        <EmptyState
                            title="Nous n'avons actuellement aucun article de blog"
                            subtitle='Vous pouvez créer un nouvel article de blog en cliquant sur le bouton "Créer un article" en haut à droite.'
                        />
                    </Grid>
                )}

                {!loading && data?.length > 0 && (
                    data.map(item => (
                        <Grid item xs={12} sm={6} md={4} key={item._id} data-testid={`blog-card-${item._id}`}>
                            <BlogCard
                                data={item}
                                handleOpenDialog={(_id) => handleOpenDialog(_id)}
                                updateBlogStatusMutate={(_id, status) => updateBlogStatusMutate(_id, status)}
                                isLoading={isLoading}
                                readOnly={readOnly}
                            />
                        </Grid>
                    ))
                )}
            </Grid>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                disableEscapeKeyDown={deleteLoading}
            >
                <DialogTitle>Supprimer l'article ?</DialogTitle>
                <DialogContent>
                    <Typography>Êtes-vous sûr de vouloir supprimer cet article ? Cette action est irréversible.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} variant="text" color="secondary" disabled={deleteLoading}>Annuler</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={18} /> : null}
                    >
                        {deleteLoading ? 'Suppression...' : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BlogListGrid;
